<ion-content>
  <div class="constrained-content">
    <ion-grid class="constrained-content">
      <ion-row>
        <ion-col size-md="8" size="12">
          <!-- Show loading state while initially loading -->
          <div *ngIf="isInitialLoading" class="loading-state">
            <ion-card class="outline-card loading-card">
              <ion-card-content>
                <ion-spinner
                  name="crescent"
                  class="loading-spinner"
                ></ion-spinner>
                <h2>Loading portal information...</h2>
                <p class="loading-text">
                  Please wait while we check your portal configuration.
                </p>
              </ion-card-content>
            </ion-card>
          </div>

          <!-- Portal Form (Create only) -->
          <div *ngIf="(!portal && !isInitialLoading) || isCreatingPortal">
            <ion-card class="portal-card">
              <ion-card-header>
                <ion-card-title>Portal Configuration Settings</ion-card-title>
                <div class="portal-docs-link">
                  <a
                    [href]="portalDocsUrl"
                    target="_blank"
                    class="learn-more-link"
                  >
                    <ion-icon name="information-circle-outline"></ion-icon>
                    {{
                      config.isExternal
                        ? "External portals are support portals for external customers"
                        : "Internal portals are support portals for internal employees"
                    }}
                  </a>
                </div>
              </ion-card-header>
              <ion-card-content>
                <form (ngSubmit)="submitPortalForm()" novalidate>
                  <div class="portal-details">
                    <!-- Logo and Primary Color Row -->
                    <div class="form-layout">
                      <div class="form-main-section">
                        <!-- Logo -->
                        <div class="detail-item logo-section">
                          <ion-label class="detail-label">
                            Logo
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.logo,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div class="logo-upload-section">
                            <div class="logo-preview-container">
                              <div class="notification-icon logo-preview-box">
                                <!-- Loading state -->
                                <div
                                  *ngIf="isUploadingLogo"
                                  class="logo-loading"
                                >
                                  <ion-spinner
                                    name="crescent"
                                    class="logo-spinner"
                                  ></ion-spinner>
                                </div>
                                <!-- Upload button when no logo and not uploading -->
                                <ion-button
                                  slot="end"
                                  size="full"
                                  fill="clear"
                                  (click)="uploadPortalLogo()"
                                  class="icon-upload-button"
                                  *ngIf="
                                    !portalForm.supportLogo && !isUploadingLogo
                                  "
                                >
                                  <ion-icon
                                    name="cloud-upload-outline"
                                  ></ion-icon>
                                </ion-button>
                                <!-- Logo image when exists and not uploading -->
                                <img
                                  *ngIf="
                                    portalForm.supportLogo && !isUploadingLogo
                                  "
                                  [src]="portalForm.supportLogo"
                                  class="logo-image"
                                  (click)="uploadPortalLogo()"
                                  alt="Portal Logo"
                                />
                              </div>
                              <div
                                class="logo-actions"
                                *ngIf="portalForm.supportLogo"
                              >
                                <ion-button
                                  size="small"
                                  fill="clear"
                                  (click)="uploadPortalLogo()"
                                >
                                  <ion-icon
                                    name="cloud-upload-outline"
                                  ></ion-icon>
                                  <span>Upload new logo</span>
                                </ion-button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Projects Section -->
                    <div class="projects-section">
                      <!-- Projects Title (always shown) -->
                      <div class="detail-item projects-section">
                        <ion-label class="detail-label">
                          Enabled Projects
                          <ion-icon
                            name="help-circle-outline"
                            class="help-icon"
                            ngxTippy
                            [tippyProps]="{
                              content: tooltips.projects,
                              placement: 'top',
                              arrow: false,
                              touch: false
                            }"
                          ></ion-icon>
                        </ion-label>

                        <!-- Projects List (when projects exist) -->
                        <div
                          class="projects-checkbox-list projects-list"
                          *ngIf="projects.length > 0"
                        >
                          <div
                            *ngFor="let project of projects"
                            class="project-checkbox-item project-item"
                            [class.disabled]="
                              hasProjectNoTicketTypes(project.id)
                            "
                          >
                            <ion-checkbox
                              [value]="project.id"
                              [checked]="
                                portalForm.selectedProjectIds.includes(
                                  project.id
                                )
                              "
                              [disabled]="hasProjectNoTicketTypes(project.id)"
                              (ionChange)="
                                onProjectCheckboxChange(project.id, $event)
                              "
                              class="project-checkbox"
                            ></ion-checkbox>
                            <ion-label class="project-label">{{
                              project.name
                            }}</ion-label>
                            <!-- Warning icon for projects without ticket types -->
                            <div
                              *ngIf="hasProjectNoTicketTypes(project.id)"
                              class="project-warning"
                            >
                              <ion-icon
                                name="warning-outline"
                                class="warning-icon project-warning-icon"
                              ></ion-icon>
                              <p>
                                Project has no ticket types - fix
                                <span
                                  (click)="navigateToTicketTypes(project.id)"
                                  class="clickable-link"
                                  >here</span
                                >
                              </p>
                            </div>
                          </div>
                        </div>

                        <!-- No Projects Warning (when no projects exist) -->
                        <div
                          class="warning-message"
                          *ngIf="projects.length === 0"
                        >
                          <ion-icon
                            name="warning-outline"
                            class="warning-icon"
                          ></ion-icon>
                          <span
                            >No {{ projectTypeName }} projects are
                            available.</span
                          >
                          <a
                            (click)="navigateToProjectCreation()"
                            class="project-link"
                          >
                            Create a {{ projectTypeName }} project
                          </a>
                        </div>
                      </div>
                    </div>
                    <!-- Slack Channel -->
                    <div class="slack-channel-section">
                      <div class="detail-item">
                        <ion-label class="detail-label">
                          Default Slack Channel
                          <ion-icon
                            name="help-circle-outline"
                            class="help-icon"
                            ngxTippy
                            [tippyProps]="{
                              content: tooltips.slackChannelId,
                              placement: 'top',
                              arrow: false,
                              touch: false
                            }"
                          ></ion-icon>
                        </ion-label>
                        <div class="slack-channel-input-container">
                          <!-- Channel selector -->
                          <div
                            (click)="
                              $event.preventDefault(); $event.stopPropagation()
                            "
                          >
                            <app-channel-selector
                              [channel]="selectedSlackChannel"
                              (channelDidChange)="onSlackChannelChanged($event)"
                              [initialButtonText]="'Select a Slack channel'"
                              [hideUnlinkButton]="true"
                              [hideLinkedToText]="false"
                            ></app-channel-selector>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Allow Direct Ticket Creation and Create Portal Button -->
                    <div class="toggle-save-section">
                      <div class="toggle-section">
                        <ion-label class="detail-label">
                          Allow Direct Ticket Creation
                          <ion-icon
                            name="help-circle-outline"
                            class="help-icon"
                            ngxTippy
                            [tippyProps]="{
                              content: tooltips.allowDirectTicketCreation,
                              placement: 'top',
                              arrow: false,
                              touch: false
                            }"
                          ></ion-icon>
                        </ion-label>
                        <label class="switch">
                          <input
                            type="checkbox"
                            [(ngModel)]="portalForm.allowDirectTicketCreation"
                            name="allowDirectTicketCreation"
                          />
                          <span class="slider"></span>
                        </label>
                      </div>
                      <ion-button
                        (click)="submitPortalForm()"
                        [disabled]="
                          isLoading ||
                          isDnsTesting ||
                          isTlsTesting ||
                          isUploadingLogo
                        "
                        size="default"
                      >
                        <ion-spinner
                          *ngIf="isLoading"
                          name="crescent"
                          class="loading-button-spinner"
                        ></ion-spinner>
                        <span *ngIf="!isLoading">Create Portal</span>
                      </ion-button>
                    </div>
                  </div>
                </form>
              </ion-card-content>
            </ion-card>
          </div>

          <!-- Show existing portal when portal exists -->
          <div *ngIf="!isInitialLoading && portal && !isCreatingPortal">
            <!-- Portal Details -->
            <div class="portal-details">
              <ion-card class="portal-card">
                <ion-card-header>
                  <ion-card-title>Portal Configuration Settings</ion-card-title>
                  <div class="portal-docs-link">
                    <a
                      [href]="portalDocsUrl"
                      target="_blank"
                      class="learn-more-link"
                    >
                      <ion-icon name="information-circle-outline"></ion-icon>
                      {{
                        config.isExternal
                          ? "External portals are support portals for external customers"
                          : "Internal portals are support portals for internal employees"
                      }}
                    </a>
                  </div>
                </ion-card-header>
                <ion-card-content>
                  <form (ngSubmit)="submitPortalForm()" novalidate>
                    <div class="portal-details">
                      <div class="form-layout">
                        <div class="form-main-section">
                          <!-- Logo and Primary Color Row -->
                          <div class="logo-color-row">
                            <!-- Logo -->
                            <div class="detail-item logo-section">
                              <ion-label class="detail-label">
                                Logo
                                <ion-icon
                                  name="help-circle-outline"
                                  class="help-icon"
                                  ngxTippy
                                  [tippyProps]="{
                                    content: tooltips.logo,
                                    placement: 'top',
                                    arrow: false,
                                    touch: false
                                  }"
                                ></ion-icon>
                              </ion-label>
                              <div class="logo-upload-section">
                                <div class="logo-preview-container">
                                  <div
                                    class="notification-icon logo-preview-box"
                                  >
                                    <!-- Loading state -->
                                    <div
                                      *ngIf="isUploadingLogo"
                                      class="logo-loading"
                                    >
                                      <ion-spinner
                                        name="crescent"
                                        class="logo-spinner"
                                      ></ion-spinner>
                                    </div>
                                    <!-- Upload button when no logo and not uploading -->
                                    <ion-button
                                      slot="end"
                                      size="full"
                                      fill="clear"
                                      (click)="uploadPortalLogo()"
                                      class="icon-upload-button"
                                      *ngIf="
                                        !portalForm.supportLogo &&
                                        !isUploadingLogo
                                      "
                                    >
                                      <ion-icon
                                        name="cloud-upload-outline"
                                      ></ion-icon>
                                    </ion-button>
                                    <!-- Logo image when exists and not uploading -->
                                    <img
                                      *ngIf="
                                        portalForm.supportLogo &&
                                        !isUploadingLogo
                                      "
                                      [src]="portalForm.supportLogo"
                                      class="logo-image"
                                      (click)="uploadPortalLogo()"
                                      alt="Portal Logo"
                                    />
                                  </div>
                                  <div
                                    class="logo-actions"
                                    *ngIf="portalForm.supportLogo"
                                  >
                                    <ion-button
                                      size="small"
                                      fill="clear"
                                      (click)="uploadPortalLogo()"
                                    >
                                      <ion-icon
                                        name="cloud-upload-outline"
                                      ></ion-icon>
                                      <span>Upload new logo</span>
                                    </ion-button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Projects Section -->
                      <div class="projects-section">
                        <!-- Projects Title (always shown) -->
                        <div class="detail-item projects-section">
                          <ion-label class="detail-label">
                            Enabled Projects
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.projects,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>

                          <!-- Projects List (when projects exist) -->
                          <div
                            class="projects-checkbox-list projects-list"
                            *ngIf="projects.length > 0"
                          >
                            <div
                              *ngFor="let project of projects"
                              class="project-checkbox-item project-item"
                              [class.disabled]="
                                hasProjectNoTicketTypes(project.id)
                              "
                            >
                              <ion-checkbox
                                [value]="project.id"
                                [checked]="
                                  portalForm.selectedProjectIds.includes(
                                    project.id
                                  )
                                "
                                [disabled]="hasProjectNoTicketTypes(project.id)"
                                (ionChange)="
                                  onProjectCheckboxChange(project.id, $event)
                                "
                                class="project-checkbox"
                              ></ion-checkbox>
                              <ion-label class="project-label">{{
                                project.name
                              }}</ion-label>
                              <!-- Warning icon for projects without ticket types -->
                              <div
                                *ngIf="hasProjectNoTicketTypes(project.id)"
                                class="project-warning"
                              >
                                <ion-icon
                                  name="warning-outline"
                                  class="warning-icon project-warning-icon"
                                ></ion-icon>
                                <p>
                                  Project has no ticket types - fix
                                  <span
                                    (click)="navigateToTicketTypes(project.id)"
                                    class="clickable-link"
                                    >here</span
                                  >
                                </p>
                              </div>
                            </div>
                          </div>

                          <!-- No Projects Warning (when no projects exist) -->
                          <div
                            class="warning-message"
                            *ngIf="projects.length === 0"
                          >
                            <ion-icon
                              name="warning-outline"
                              class="warning-icon"
                            ></ion-icon>
                            <span
                              >No {{ projectTypeName }} projects are
                              available.</span
                            >
                            <a
                              (click)="navigateToProjectCreation()"
                              class="project-link"
                            >
                              Create a {{ projectTypeName }} project
                            </a>
                          </div>
                        </div>
                      </div>
                      <!-- Slack Channel -->
                      <div class="slack-channel-section">
                        <div class="detail-item">
                          <ion-label class="detail-label">
                            Default Slack Channel
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.slackChannelId,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div class="slack-channel-input-container">
                            <!-- Channel selector -->
                            <div
                              (click)="
                                $event.preventDefault();
                                $event.stopPropagation()
                              "
                            >
                              <app-channel-selector
                                [channel]="selectedSlackChannel"
                                (channelDidChange)="
                                  onSlackChannelChanged($event)
                                "
                                [initialButtonText]="'Select a Slack channel'"
                                [hideUnlinkButton]="true"
                                [hideLinkedToText]="true"
                              ></app-channel-selector>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Allow Direct Ticket Creation and Save Button -->
                      <div class="toggle-save-section">
                        <div class="toggle-section">
                          <ion-label class="detail-label">
                            Allow Direct Ticket Creation
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.allowDirectTicketCreation,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <label class="switch">
                            <input
                              type="checkbox"
                              [(ngModel)]="portalForm.allowDirectTicketCreation"
                              name="allowDirectTicketCreation"
                            />
                            <span class="slider"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div class="save-buttons">
                      <ion-button
                        (click)="submitPortalForm()"
                        [disabled]="
                          isLoading ||
                          isDnsTesting ||
                          isTlsTesting ||
                          isUploadingLogo ||
                          !hasTopCardChanged
                        "
                        class="save-button"
                        [class.save-button--has-changes]="hasTopCardChanged"
                        size="default"
                      >
                        <ion-spinner
                          *ngIf="isLoading"
                          name="crescent"
                          class="loading-button-spinner"
                        ></ion-spinner>
                        <span *ngIf="!isLoading">Save</span>
                      </ion-button>
                      <ion-button
                        *ngIf="hasTopCardChanged"
                        type="button"
                        (click)="cancelPortalForm()"
                        fill="clear"
                        color="dark"
                        [disabled]="isLoading"
                      >
                        Clear Changes
                      </ion-button>
                    </div>
                  </form>
                </ion-card-content>
              </ion-card>

              <ion-card class="portal-card">
                <ion-card-header>
                  <ion-card-title>Portal Customization</ion-card-title>
                </ion-card-header>
                <ion-card-content>
                  <!-- Portal Customization -->
                  <div class="portal-customization-section-container flexed">
                    <!-- Primary Color -->
                    <div class="detail-item color-section">
                      <ion-label class="detail-label">
                        Primary Color
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.primaryColor,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div class="color-upload-section">
                        <div
                          class="color-preview-container color-preview-wrapper"
                        >
                          <div
                            class="color-preview color-preview-box"
                            [style.background-color]="portalForm.primaryColor"
                          >
                            <input
                              #colorInput
                              type="color"
                              [(ngModel)]="portalForm.primaryColor"
                              name="primaryColor"
                              class="color-input"
                            />
                          </div>
                          <ion-button
                            size="small"
                            fill="clear"
                            (click)="colorInput.click()"
                            class="color-button"
                          >
                            <ion-icon name="color-palette-outline"></ion-icon>
                            <span>Change primary color</span>
                          </ion-button>
                        </div>
                      </div>
                    </div>
                    <!-- Dark Theme Accent Color -->
                    <div class="detail-item color-section">
                      <ion-label class="detail-label">
                        Dark Theme Accent Color
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.darkThemeAccentColor,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div class="color-upload-section">
                        <div
                          class="color-preview-container color-preview-wrapper"
                        >
                          <div
                            class="color-preview color-preview-box"
                            [style.background-color]="
                              portalForm.darkThemeAccentColor
                            "
                          >
                            <input
                              #darkColorInput
                              type="color"
                              [(ngModel)]="portalForm.darkThemeAccentColor"
                              name="darkThemeAccentColor"
                              class="color-input"
                            />
                          </div>
                          <ion-button
                            size="small"
                            fill="clear"
                            (click)="darkColorInput.click()"
                            class="color-button"
                          >
                            <ion-icon name="color-palette-outline"></ion-icon>
                            <span>Change accent color</span>
                          </ion-button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="portal-customization-section">
                    <!-- Custom Domain -->
                    <div class="detail-item">
                      <ion-label class="detail-label">
                        Custom Domain
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.customDomain,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>

                      <!-- Custom Domain Input (always visible) -->
                      <div
                        class="custom-domain-input-container"
                        style="
                          margin-top: 16px;
                          padding: 16px;
                          background: var(--ion-color-light);
                          border: 1px solid var(--ion-border-color);
                          border-radius: 8px;
                        "
                      >
                        <div class="input-container">
                          <ion-input
                            class="input-field custom-domain-input"
                            [(ngModel)]="portalForm.customDomain"
                            name="customDomain"
                            type="text"
                            placeholder="e.g. support.yourcompany.com"
                            (input)="onCustomDomainInput($event)"
                            (blur)="onCustomDomainInput($event)"
                            maxlength="253"
                            clearInput="true"
                            (ionClear)="clearCustomDomain()"
                          ></ion-input>
                        </div>
                        <div
                          class="custom-domain-help"
                          *ngIf="portalForm.customDomain"
                        >
                          <div class="dns-config-instructions">
                            <p class="dns-title">DNS Configuration Required:</p>
                            <p class="dns-step">
                              1. Add a <strong>CNAME</strong> record in your DNS
                              provider:
                            </p>
                            <div class="dns-record-example">
                              <span class="dns-label">Name:</span>
                              <code>support</code> (or your preferred subdomain)
                            </div>
                            <div class="dns-record-example">
                              <span class="dns-label">Value:</span>
                              <code>portal.{{ rootDomain }}</code>
                            </div>
                            <div class="dns-record-example">
                              <span class="dns-label">TTL:</span>
                              <code>3600</code> (or default)
                            </div>
                            <p class="dns-note">
                              Note: DNS changes can take up to 48 hours to
                              propagate globally.
                            </p>
                          </div>

                          <!-- Test Actions for Draft Portals -->
                          <div
                            class="test-actions"
                            *ngIf="
                              portalForm.customDomain &&
                              portalForm.customDomain.trim()
                            "
                          >
                            <ion-button
                              fill="outline"
                              color="tertiary"
                              (click)="testDnsConnection(portal)"
                              [disabled]="
                                isLoading ||
                                isDnsTesting ||
                                isTlsTesting ||
                                isUploadingLogo
                              "
                              class="test-button test-button--small"
                            >
                              <ion-spinner
                                *ngIf="isDnsTesting"
                                name="crescent"
                                class="loading-button-spinner"
                              ></ion-spinner>
                              <ion-icon
                                *ngIf="!isDnsTesting"
                                name="checkmark-circle-outline"
                              ></ion-icon>
                              Test DNS
                            </ion-button>

                            <ion-button
                              fill="outline"
                              color="secondary"
                              (click)="testTlsCertificate(portal)"
                              [disabled]="
                                isLoading ||
                                isDnsTesting ||
                                isTlsTesting ||
                                isUploadingLogo
                              "
                              class="test-button test-button--small"
                            >
                              <ion-spinner
                                *ngIf="isTlsTesting"
                                name="crescent"
                                class="loading-button-spinner"
                              ></ion-spinner>
                              <ion-icon
                                *ngIf="!isTlsTesting"
                                name="checkmark-circle-outline"
                              ></ion-icon>
                              Test TLS
                            </ion-button>
                          </div>

                          <!-- DNS Test Steps Progress -->
                          <div
                            class="dns-test-steps"
                            *ngIf="
                              isDnsTesting &&
                              currentTestingPortal?.id === portal.id
                            "
                          >
                            <div class="test-steps-container">
                              <div
                                *ngFor="let step of dnsTestSteps; let i = index"
                                class="test-step"
                                [class.active]="i === currentDnsStep"
                                [class.completed]="i < currentDnsStep"
                              >
                                <div class="step-icon">
                                  <ion-icon
                                    *ngIf="i < currentDnsStep"
                                    name="checkmark-circle"
                                    color="success"
                                  ></ion-icon>
                                  <ion-icon
                                    *ngIf="i === currentDnsStep"
                                    name="ellipse"
                                    color="primary"
                                  ></ion-icon>
                                  <ion-icon
                                    *ngIf="i > currentDnsStep"
                                    name="ellipse-outline"
                                    color="medium"
                                  ></ion-icon>
                                </div>
                                <div class="step-text">{{ step }}</div>
                              </div>
                            </div>
                          </div>

                          <!-- TLS Test Steps Progress -->
                          <div
                            class="dns-test-steps"
                            *ngIf="
                              isTlsTesting &&
                              currentTestingPortal?.id === portal.id
                            "
                          >
                            <div class="test-steps-container">
                              <div
                                *ngFor="let step of tlsTestSteps; let i = index"
                                class="test-step"
                                [class.active]="i === currentTlsStep"
                                [class.completed]="i < currentTlsStep"
                              >
                                <div class="step-icon">
                                  <ion-icon
                                    *ngIf="i < currentTlsStep"
                                    name="checkmark-circle"
                                    color="success"
                                  ></ion-icon>
                                  <ion-icon
                                    *ngIf="i === currentTlsStep"
                                    name="ellipse"
                                    color="primary"
                                  ></ion-icon>
                                  <ion-icon
                                    *ngIf="i > currentTlsStep"
                                    name="ellipse-outline"
                                    color="medium"
                                  ></ion-icon>
                                </div>
                                <div class="step-text">{{ step }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Homepage Metadata -->
                    <div class="detail-item">
                      <ion-label class="detail-label">
                        Homepage Hero Section
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.homepageMetadata,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div
                        class="homepage-metadata-fields"
                        style="
                          margin-top: 16px;
                          padding: 16px;
                          background: var(--ion-color-light);
                          border: 1px solid var(--ion-border-color);
                          border-radius: 8px;
                        "
                      >
                        <div class="metadata-field">
                          <ion-label class="input-label">Title</ion-label>
                          <ion-input
                            [(ngModel)]="portalForm.homepageMetadata.title"
                            name="homepageMetadataTitle"
                            placeholder="Enter homepage title"
                            maxlength="100"
                            class="input-field"
                          ></ion-input>
                        </div>
                        <div class="metadata-field">
                          <ion-label class="input-label">Description</ion-label>
                          <ion-textarea
                            [(ngModel)]="
                              portalForm.homepageMetadata.description
                            "
                            name="homepageMetadataDescription"
                            placeholder="Enter homepage description"
                            maxlength="300"
                            rows="3"
                            class="input-field"
                          ></ion-textarea>
                        </div>
                      </div>
                    </div>

                    <!-- Custom Links -->
                    <div class="detail-item">
                      <ion-label class="detail-label">
                        Custom Links
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.customLinks,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div class="custom-links-container">
                        <div
                          class="links-list"
                          *ngIf="portalForm.customLinks.length > 0"
                        >
                          <div
                            *ngFor="
                              let link of portalForm.customLinks;
                              let i = index
                            "
                            class="link-item"
                          >
                            <ion-button
                              size="small"
                              fill="clear"
                              color="dark"
                              (click)="selectCustomLinkIcon(i)"
                              class="add-button"
                              ngxTippy
                              style="height: 50px; width: 50px"
                              [tippyProps]="{
                                content: 'Set icon for this link',
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            >
                              <div class="icon-preview">
                                <div *ngIf="getIconComponent(link.icon)">
                                  <lucide-icon
                                    [img]="getIconComponent(link.icon)"
                                    class="selected-icon"
                                  ></lucide-icon>
                                </div>
                              </div>
                            </ion-button>
                            <div class="link-inputs">
                              <ion-input
                                [(ngModel)]="link.title"
                                placeholder="Link title"
                                class="no-margin input-field"
                              ></ion-input>
                              <ion-input
                                [(ngModel)]="link.link"
                                placeholder="URL"
                                class="no-margin input-field"
                              ></ion-input>
                            </div>
                            <ion-button
                              size="small"
                              fill="clear"
                              color="dark"
                              (click)="removeCustomLink(i)"
                              class="item-accessory-button"
                            >
                              <ion-icon name="close-outline"></ion-icon>
                            </ion-button>
                          </div>
                        </div>
                        <ion-button
                          size="small"
                          fill="clear"
                          (click)="addCustomLink()"
                          class="add-button"
                        >
                          <ion-icon name="add-circle-outline"></ion-icon>
                          <span>Add Custom Link</span>
                        </ion-button>
                      </div>
                    </div>

                    <!-- Featured Collections -->
                    <div class="detail-item">
                      <ion-label class="detail-label">
                        Featured Knowledge Base Collections
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.featuredCollections,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div class="featured-collections-container">
                        <div
                          class="featured-collections-list"
                          *ngIf="portalForm.featuredCollections.length > 0"
                        >
                          <ion-reorder-group
                            (ionItemReorder)="onReorder($event)"
                            [disabled]="false"
                          >
                            <div
                              *ngFor="
                                let collectionId of portalForm.featuredCollections;
                                let i = index;
                                trackBy: trackByCollectionId
                              "
                              class="featured-collection-item"
                            >
                              <ion-reorder slot="start"></ion-reorder>
                              <div class="collection-info">
                                <ion-label class="collection-name">
                                  {{ getCollectionName(collectionId) }}
                                </ion-label>
                                <ion-text color="medium" class="project-name">
                                  {{ getCollectionProjectName(collectionId) }}
                                </ion-text>
                              </div>
                              <ion-button
                                size="small"
                                fill="clear"
                                color="danger"
                                (click)="removeFeaturedCollection(collectionId)"
                                class="remove-button"
                              >
                                <ion-icon
                                  name="close-circle-outline"
                                ></ion-icon>
                                <span>Remove</span>
                              </ion-button>
                            </div>
                          </ion-reorder-group>
                        </div>
                        <ion-button
                          size="small"
                          fill="clear"
                          (click)="showCollectionSelector()"
                          class="add-button"
                        >
                          <ion-icon name="add-circle-outline"></ion-icon>
                          <span>Add Featured Collection</span>
                        </ion-button>
                      </div>
                    </div>

                    <!-- Collections Order -->
                    <div class="detail-item">
                      <div class="toggle-section">
                        <ion-label class="detail-label">
                          Manually Order & Display Collections?
                          <ion-icon
                            name="help-circle-outline"
                            class="help-icon"
                            ngxTippy
                            [tippyProps]="{
                              content:
                                'Define the order in which collections appear on the documentation page. If empty, collections will be shown alphabetically.',
                              placement: 'top',
                              arrow: false,
                              touch: false
                            }"
                          ></ion-icon>
                        </ion-label>
                        <label class="switch">
                          <input
                            type="checkbox"
                            [(ngModel)]="isManualCollectionsOrderEnabled"
                            (change)="onManualCollectionsOrderToggle()"
                            name="isManualCollectionsOrderEnabled"
                          />
                          <span class="slider"></span>
                        </label>
                      </div>

                      <div
                        class="featured-collections-container"
                        *ngIf="isManualCollectionsOrderEnabled"
                      >
                        <div
                          class="featured-collections-list"
                          *ngIf="portalForm.collections.length > 0"
                        >
                          <ion-reorder-group
                            (ionItemReorder)="onCollectionsReorder($event)"
                            [disabled]="false"
                          >
                            <div
                              *ngFor="
                                let collectionId of portalForm.collections;
                                let i = index;
                                trackBy: trackByCollectionIdForOrder
                              "
                              class="featured-collection-item"
                            >
                              <ion-reorder slot="start"></ion-reorder>
                              <div class="collection-info">
                                <ion-label class="collection-name">
                                  {{ getCollectionName(collectionId) }}
                                </ion-label>
                                <ion-text color="medium" class="project-name">
                                  {{ getCollectionProjectName(collectionId) }}
                                </ion-text>
                              </div>
                              <ion-button
                                size="small"
                                fill="clear"
                                color="danger"
                                (click)="
                                  removeCollectionFromOrder(collectionId)
                                "
                                class="remove-button"
                              >
                                <ion-icon
                                  name="close-circle-outline"
                                ></ion-icon>
                                <span>Remove</span>
                              </ion-button>
                            </div>
                          </ion-reorder-group>
                        </div>
                        <ion-button
                          size="small"
                          fill="clear"
                          (click)="showCollectionsOrderSelector()"
                          class="add-button"
                        >
                          <ion-icon name="add-circle-outline"></ion-icon>
                          <span>{{
                            portalForm.collections.length > 0
                              ? "Add More Collections"
                              : "Re-order Collections"
                          }}</span>
                        </ion-button>
                      </div>
                    </div>
                  </div>

                  <div class="save-buttons">
                    <ion-button
                      (click)="submitPortalForm()"
                      [disabled]="
                        isLoading ||
                        isDnsTesting ||
                        isTlsTesting ||
                        isUploadingLogo ||
                        !hasPortalCustomizationChanged
                      "
                      class="save-button"
                      [class.save-button--has-changes]="
                        hasPortalCustomizationChanged
                      "
                      size="default"
                    >
                      <ion-spinner
                        *ngIf="isLoading"
                        name="crescent"
                        class="loading-button-spinner"
                      ></ion-spinner>
                      <span *ngIf="!isLoading">Save</span>
                    </ion-button>
                    <ion-button
                      *ngIf="hasPortalCustomizationChanged"
                      type="button"
                      (click)="revertPortalCustomizationChanges()"
                      fill="clear"
                      color="dark"
                      [disabled]="isLoading"
                    >
                      Clear Changes
                    </ion-button>
                  </div>
                </ion-card-content>
              </ion-card>

              <!-- Authentication Card -->
              <ion-card class="portal-card">
                <ion-card-header>
                  <ion-card-title>Authentication</ion-card-title>
                </ion-card-header>
                <ion-card-content>
                  <div>
                    <!-- Magic Link Toggle -->
                    <div
                      class="toggle-section"
                      style="display: flex; margin-bottom: 16px"
                    >
                      <ion-label class="detail-label"
                        >Email Magic Link</ion-label
                      >
                      <label class="switch" style="margin-left: 12px">
                        <input
                          type="checkbox"
                          [checked]="getLoginMethod('magicLink')?.enabled"
                          (change)="
                            setLoginMethodEnabled(
                              'magicLink',
                              $event.target.checked
                            )
                          "
                          name="magicLinkEnabled"
                        />
                        <span class="slider"></span>
                      </label>
                    </div>

                    <!-- Magic Link Configuration (shown when enabled) -->
                    <div
                      *ngIf="getLoginMethod('magicLink')?.enabled"
                      class="magic-link-config-container"
                      style="
                        margin-top: 16px;
                        padding: 16px;
                        background: var(--ion-color-light);
                        border: 1px solid var(--ion-border-color);
                        border-radius: 8px;
                      "
                    >
                      <!-- Show sender info -->
                      <div>
                        <p
                          style="
                            margin: 0;
                            color: var(--ion-text-color);
                            font-size: 14px;
                          "
                        >
                          Magic Links will be sent from
                          <strong>{{ getSenderEmail() }}</strong>
                        </p>
                        <div *ngIf="verifiedEmailDomains.length === 0">
                          <p
                            style="
                              margin: 8px 0 0 0;
                              color: var(--ion-color-medium);
                              font-size: 12px;
                            "
                          >
                            Configure a custom sender by
                            <a (click)="navigateToEmailDomainVerification()"
                              >verifying an email domain</a
                            >
                          </p>
                        </div>
                        <p
                          *ngIf="verifiedEmailDomains.length > 0"
                          style="
                            margin: 8px 0 0 0;
                            color: var(--ion-color-medium);
                            font-size: 12px;
                          "
                        >
                          Select a domain below to use a custom sender email
                          address, or
                          <a (click)="navigateToEmailDomainVerification()"
                            >verify a new email domain</a
                          >
                        </p>
                      </div>

                      <!-- Email input fields when verified domains exist -->
                      <div *ngIf="verifiedEmailDomains.length > 0">
                        <div
                          class="email-field"
                          style="margin-top: 12px; margin-bottom: 12px"
                        >
                          <div class="email-input-flex">
                            <div class="email-prefix-container">
                              <ion-input
                                class="input-field email-prefix-input"
                                [(ngModel)]="signInEmailPrefix"
                                (ionInput)="onEmailPrefixChange($event)"
                                name="signInEmailPrefix"
                                type="text"
                                placeholder="noreply"
                                maxlength="64"
                              ></ion-input>
                            </div>
                            <div class="email-at-symbol">&#64;</div>
                            <div class="email-domain-container">
                              <ion-select
                                class="email-domain-select"
                                [(ngModel)]="selectedEmailDomain"
                                (ionChange)="onEmailDomainChange()"
                                name="selectedEmailDomain"
                                placeholder="Select domain"
                                interface="popover"
                              >
                                <ion-select-option
                                  *ngFor="let domain of verifiedEmailDomains"
                                  [value]="domain.domain"
                                >
                                  {{ domain.domain }}
                                </ion-select-option>
                              </ion-select>
                            </div>
                            <div
                              *ngIf="
                                portal?.signInEmailSender ||
                                (portalForm.signInEmailSender &&
                                  isEmailFromVerifiedDomain(
                                    portalForm.signInEmailSender
                                  ))
                              "
                            >
                              <ion-button
                                size="small"
                                color="danger"
                                fill="clear"
                                (click)="clearCustomEmailSender()"
                              >
                                <ion-icon name="close-outline"></ion-icon>
                              </ion-button>
                            </div>
                          </div>
                          <div
                            *ngIf="
                              portalForm.signInEmailSender &&
                              !isEmailFromVerifiedDomain(
                                portalForm.signInEmailSender
                              )
                            "
                            class="validation-error"
                            style="margin-top: 8px"
                          >
                            <p>
                              {{ portalForm.signInEmailSender }} is not from a
                              verified domain
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- SSO Toggle -->
                    <div
                      class="toggle-section"
                      style="
                        display: flex;
                        margin-top: 16px;
                        margin-bottom: 16px;
                      "
                    >
                      <ion-label class="detail-label"
                        >Single Sign-On (SSO)</ion-label
                      >
                      <label class="switch" style="margin-left: 12px">
                        <input
                          type="checkbox"
                          [checked]="getLoginMethod('sso')?.enabled"
                          (change)="
                            setLoginMethodEnabled('sso', $event.target.checked)
                          "
                          name="ssoEnabled"
                        />
                        <span class="slider"></span>
                      </label>
                    </div>

                    <!-- SSO Configuration Fields (shown when SSO is enabled) -->
                    <div
                      *ngIf="
                        getLoginMethod('sso')?.enabled &&
                        getLoginMethod('sso')?.sso
                      "
                      class="sso-config-container"
                    >
                      <div class="sso-field" style="margin-bottom: 12px">
                        <ion-label class="input-label">Client ID</ion-label>
                        <ion-input
                          [(ngModel)]="ssoClientId"
                          name="ssoClientId"
                          placeholder="Enter client ID"
                          class="input-field"
                          maxlength="255"
                        ></ion-input>
                      </div>

                      <div class="sso-field" style="margin-bottom: 12px">
                        <ion-label class="input-label">Issuer URL</ion-label>
                        <ion-input
                          [(ngModel)]="ssoIssuerUrl"
                          name="ssoIssuerUrl"
                          placeholder="https://example.com/issuer"
                          class="input-field"
                          maxlength="255"
                        ></ion-input>
                      </div>

                      <div class="sso-field" style="margin-bottom: 12px">
                        <ion-label class="input-label">Client Secret</ion-label>
                        <div style="position: relative">
                          <ion-input
                            [(ngModel)]="ssoClientSecret"
                            name="ssoClientSecret"
                            placeholder="Enter client secret"
                            [type]="showSsoSecret ? 'text' : 'password'"
                            class="input-field"
                            maxlength="255"
                          ></ion-input>
                          <ion-button
                            fill="clear"
                            size="small"
                            style="
                              position: absolute;
                              right: 8px;
                              top: 50%;
                              transform: translateY(-50%);
                              --padding-start: 8px;
                              --padding-end: 8px;
                              height: 32px;
                              margin: 0;
                              z-index: 10;
                            "
                            (click)="toggleSsoSecretVisibility()"
                          >
                            <ion-icon
                              [name]="
                                showSsoSecret
                                  ? 'eye-outline'
                                  : 'eye-off-outline'
                              "
                              style="color: var(--ion-color-medium)"
                            ></ion-icon>
                          </ion-button>
                        </div>
                      </div>

                      <div class="sso-field" style="margin-bottom: 12px">
                        <ion-label class="input-label"
                          >Redirect URL:
                        </ion-label>
                        <ion-label class="code">
                          {{
                            generatePortalUrl(portal || {}) + "/__/auth/handler"
                          }}
                        </ion-label>

                        <ion-button
                          fill="clear"
                          class="btn-small button-icon"
                          (click)="
                            copyToClipboard(
                              generatePortalUrl(portal || {}) +
                                '/__/auth/handler'
                            )
                          "
                        >
                          <ion-icon
                            name="copy-outline"
                            class="padded-icon"
                          ></ion-icon>
                          Copy to clipboard
                        </ion-button>
                      </div>
                    </div>

                    <div class="save-buttons">
                      <ion-button
                        (click)="saveAuthentication()"
                        [disabled]="
                          isSavingAuthentication || !hasAuthenticationChanged
                        "
                        class="save-button"
                        [class.save-button--has-changes]="
                          hasAuthenticationChanged
                        "
                        size="default"
                      >
                        <ion-spinner
                          *ngIf="isSavingAuthentication"
                          name="crescent"
                          class="loading-button-spinner"
                        ></ion-spinner>
                        <span *ngIf="!isSavingAuthentication">Save</span>
                      </ion-button>
                      <ion-button
                        *ngIf="hasAuthenticationChanged"
                        type="button"
                        (click)="revertAuthenticationChanges()"
                        fill="clear"
                        color="dark"
                        [disabled]="isSavingAuthentication"
                      >
                        Clear Changes
                      </ion-button>
                    </div>
                  </div>
                </ion-card-content>
              </ion-card>

              <!-- Danger Zone -->
              <ion-card class="danger-zone-card">
                <ion-card-header>
                  <ion-card-title class="danger-zone-title">
                    <ion-icon name="warning-outline" color="danger"></ion-icon>
                    Danger Zone
                  </ion-card-title>
                </ion-card-header>
                <ion-card-content>
                  <div class="danger-zone-content">
                    <!-- Impersonate User (Tenant Owners Only) -->
                    <div
                      class="danger-zone-item"
                      *ngIf="
                        portal && portal.isEnabled && authService.isTenantOwner
                      "
                    >
                      <div class="danger-zone-info">
                        <h3>Impersonate</h3>
                        <p>
                          Log into the portal as another user to experience the
                          portal as they do.
                        </p>
                      </div>
                      <div class="impersonate-container">
                        <!-- User dropdown for internal portals -->
                        <ion-item
                          *ngIf="!portal?.isExternal"
                          button
                          (click)="openUserSelector($event)"
                        >
                          <app-avatar
                            *ngIf="selectedImpersonateUser"
                            size="20"
                            [style.height]="'20px'"
                            [style.width]="'20px'"
                            [style.margin]="'0 8px 0 0'"
                            slot="start"
                            [name]="
                              selectedImpersonateUser?.name ||
                              selectedImpersonateUser?.email
                            "
                            [image]="selectedImpersonateUser?.photo"
                          ></app-avatar>
                          <ion-label>
                            {{
                              selectedImpersonateUser?.name ||
                                selectedImpersonateUser?.email ||
                                "Select user..."
                            }}
                          </ion-label>
                          <ion-icon
                            slot="end"
                            name="chevron-down-outline"
                            style="font-size: 18px"
                          ></ion-icon>
                        </ion-item>
                        <!-- Email input for external portals -->
                        <ion-input
                          *ngIf="portal?.isExternal"
                          type="email"
                          placeholder="Enter email address"
                          [(ngModel)]="impersonateEmail"
                          style="max-width: 300px"
                          (keydown.enter)="
                            impersonateByEmail(portal.id, $event)
                          "
                        ></ion-input>
                        <ion-button
                          fill="outline"
                          color="warning"
                          (click)="
                            portal?.isExternal
                              ? impersonateByEmail(portal.id, $event)
                              : impersonateSelectedUser(portal.id)
                          "
                          [disabled]="
                            (portal?.isExternal && !impersonateEmail) ||
                            (!portal?.isExternal && !selectedImpersonateUser) ||
                            isLoading ||
                            isDnsTesting ||
                            isTlsTesting ||
                            isUploadingLogo
                          "
                          class="impersonate-button"
                        >
                          <ion-icon
                            name="person-circle-outline"
                            slot="start"
                          ></ion-icon>
                          Impersonate
                        </ion-button>
                      </div>
                    </div>

                    <!-- Disable Portal for Live Portals -->
                    <div
                      class="danger-zone-item"
                      *ngIf="portal && portal.isEnabled"
                    >
                      <div class="danger-zone-info">
                        <h3>Disable Portal</h3>
                        <p>
                          Need to change custom domain settings? You'll need to
                          disable this portal first, then make your changes and
                          re-enable it.
                        </p>
                      </div>
                      <ion-button
                        fill="outline"
                        color="warning"
                        (click)="disablePortal(portal.id)"
                        [disabled]="
                          isLoading ||
                          isDnsTesting ||
                          isTlsTesting ||
                          isUploadingLogo
                        "
                        class="disable-portal-button"
                      >
                        <ion-icon
                          name="pause-circle-outline"
                          slot="start"
                        ></ion-icon>
                        Disable Portal
                      </ion-button>
                    </div>

                    <div class="danger-zone-item">
                      <div class="danger-zone-info">
                        <h3>Delete Portal</h3>
                        <p>
                          Permanently delete this portal and all its associated
                          data.<br />This action cannot be undone.
                        </p>
                      </div>
                      <ion-button
                        fill="outline"
                        color="danger"
                        (click)="deletePortal(portal.id)"
                        [disabled]="
                          isLoading ||
                          isDnsTesting ||
                          isTlsTesting ||
                          isUploadingLogo
                        "
                      >
                        <ion-icon name="trash-outline"></ion-icon>
                        Delete Portal
                      </ion-button>
                    </div>
                  </div>
                </ion-card-content>
              </ion-card>
            </div>
          </div>
        </ion-col>
        <ion-col size="4">
          <!-- Deploy & Manage Card -->
          <ion-card class="portal-card" style="position: sticky; top: 22px">
            <ion-card-header class="flex-space-between flexed-align-center">
              <ion-card-title>Deployment</ion-card-title>
              <div *ngIf="portal">
                <ion-icon
                  name="ellipse"
                  [color]="portal.isEnabled ? 'success' : 'warning'"
                  class="padded-icon"
                ></ion-icon>
                {{ portal.isEnabled ? "Live" : "Draft" }}
              </div>
            </ion-card-header>
            <ion-card-content>
              <!-- No Portal State -->
              <div class="deploy-actions-layout" *ngIf="!portal">
                <div class="empty-state-container">
                  <ion-text color="medium"
                    >No portal has been configured yet. Save your configuration
                    to create your first portal.</ion-text
                  >
                </div>
              </div>

              <!-- Portal Status (Draft or Live) -->
              <div class="deploy-actions-layout" *ngIf="portal">
                <!-- Status -->
                <div class="deploy-info-section">
                  <div class="detail-item">
                    <div class="portal-status-container">
                      <div
                        class="status-message status-message--draft"
                        *ngIf="!portal.isEnabled"
                      >
                        <p class="status-text">
                          This portal is currently in draft mode and is not yet
                          accessible to users.
                        </p>
                        <!-- Publish Button - moved inside the warning message -->
                        <div class="main-cta-section">
                          <ion-button
                            (click)="publishPortal(portal.id)"
                            [disabled]="
                              isLoading ||
                              isDnsTesting ||
                              isTlsTesting ||
                              isUploadingLogo ||
                              isPublishing
                            "
                            size="large"
                          >
                            <ion-spinner
                              *ngIf="isPublishing"
                              name="crescent"
                              class="loading-button-spinner--right"
                            ></ion-spinner>
                            <ion-icon
                              name="rocket-outline"
                              *ngIf="!isPublishing"
                              class="padded-icon"
                            ></ion-icon>
                            Publish
                          </ion-button>
                        </div>
                      </div>
                      <div
                        class="status-message status-message--live"
                        *ngIf="portal.isEnabled"
                      >
                        <p class="status-text status-text--live">
                          Access your portal at:
                          <strong
                            ><a
                              [href]="generatePortalUrl(portal)"
                              target="_blank"
                              rel="noopener"
                              class="domain-link"
                            >
                              {{ generatePortalUrl(portal) }}
                              <ion-icon name="open-outline"></ion-icon> </a
                          ></strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>
    </ion-grid>
  </div>
</ion-content>
